import { LinearFilter, LinearMipmapLinearFilter, RepeatWrapping, SRGBColorSpace, TextureLoader, type Texture } from 'three/webgpu';
import type { WorldData } from '../../world/schema';
import type { SurfaceTextures } from './surfaceFactory';

/**
 * ⭐ G1 · WORLD TEXTURE LOADING — the resolver `render/materials/catalog.ts` was written to
 * receive and never got.
 *
 * `catalog.ts` has said since R1: *"Texture references stay UNRESOLVED in R1 … the A4 cell loader
 * plugs the real resolver in."* This is that plug. Until it existed, `kind: 'texture'` assets
 * parsed and `materialFor(world, id, resolveTexture)` accepted a resolver that nothing ever
 * passed, so every world material rendered with three's 1×1 default in each slot — a complete,
 * tested, unreachable path. Sponza looked textured only because GLTFLoader was decoding textures
 * embedded in its GLB, which is not this system at all.
 *
 * ── COLOUR SPACE IS A PROPERTY OF THE SLOT, NOT THE FILE ────────────────────────────────────────
 *
 * A PNG carries no colour-space intent. `map` and `emissiveMap` hold authored colour and must be
 * decoded from sRGB; `normalMap` and `orm` hold VECTORS AND MEASUREMENTS — a tangent-space normal,
 * an occlusion/roughness/metalness triple — and decoding those from sRGB corrupts them. The error
 * is not subtle in kind but it is subtle on screen: an sRGB-decoded ORM makes everything read too
 * glossy and slightly too dark, which looks like a lighting problem and gets "fixed" in lighting.
 *
 * So the resolver takes the SLOT, and the same file requested for two slots yields two textures.
 * That is a deliberate cost (it is also vanishingly rare) and the alternative is one of the two
 * slots being wrong with no way to tell which.
 *
 * ── SAMPLER STATE IS IN THE PIPELINE CACHE KEY (surfaceFactory rule 2) ──────────────────────────
 *
 * Filters, wrap and mipmapping are set EXPLICITLY and identically for every texture, because they
 * are part of the WebGPU pipeline cache key: two textures differing only in `minFilter` fork a
 * program. Leaving them at three's defaults would work and would silently make the count of
 * programs depend on load order.
 */

/** Slots the factory binds. `orm` is ONE texture on ao + roughness + metalness (rule 4). */
export type TextureSlot = keyof SurfaceTextures;

/** Slots holding authored COLOUR. Everything else is data and stays linear. */
const SRGB_SLOTS: ReadonlySet<string> = new Set(['map', 'emissiveMap']);

const loader = new TextureLoader();

/** `(worldId, assetId, slot)` → texture. Module-level so a world re-entered reuses its uploads. */
const cache = new Map<string, Texture>();

function configure(tex: Texture, slot: TextureSlot): Texture {
  if (SRGB_SLOTS.has(slot)) tex.colorSpace = SRGBColorSpace;
  // Explicit and uniform — see the rule 2 note above.
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.magFilter = LinearFilter;
  tex.minFilter = LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = 4;
  tex.flipY = false; // glTF convention — the meshes these bind to are glTF.
  tex.needsUpdate = true;
  return tex;
}

/**
 * Build a resolver for one world. Returns `undefined` for an unknown id or a non-texture asset —
 * never throws, so the factory falls back to the archetype's default map and the surface renders
 * untextured instead of the world failing to boot (constitution rule 6).
 *
 * Loading is ASYNCHRONOUS and the Texture object is returned immediately: three uploads it when
 * the image arrives and the material picks it up on the next frame. That is what keeps a 134 MB
 * texture set off the boot's critical path — the level renders untextured for a moment and fills
 * in, rather than showing nothing until the last PNG lands.
 */
export function makeTextureResolver(
  world: Pick<WorldData, 'worldId' | 'assets'>,
  resolveUrl: (uri: string) => string,
): (assetId: string, slot: TextureSlot) => Texture | undefined {
  const warned = new Set<string>();
  return (assetId, slot) => {
    const key = `${world.worldId}|${assetId}|${slot}`;
    const hit = cache.get(key);
    if (hit) return hit;

    const asset = world.assets.find((a) => a.id === assetId);
    if (!asset || asset.kind !== 'texture') {
      if (!warned.has(assetId)) {
        warned.add(assetId);
        console.warn(`[textures] world '${world.worldId}' has no texture asset '${assetId}'`);
      }
      return undefined;
    }
    const tex = configure(
      loader.load(resolveUrl(asset.uri), undefined, undefined, () => {
        // A failed image is a warn-once, not a throw: the material keeps the archetype default.
        if (warned.has(key)) return;
        warned.add(key);
        console.warn(`[textures] '${assetId}' failed to load — ${slot} falls back to the default`);
      }),
      slot,
    );
    cache.set(key, tex);
    return tex;
  };
}

/** Drop every cached texture and free its GPU memory. Called on world teardown. */
export function clearWorldTextures(): void {
  for (const t of cache.values()) t.dispose();
  cache.clear();
}
